import { useEffect, useRef } from 'react'
import isEqual from 'lodash-es/isEqual'

import { useWidthResize } from '../../../../hooks'

import {
  getColumnMaxWidth,
  getColumnMinWidth,
  getColumnResizeLimits,
  getColumnWidth,
} from './useColumnResize.utils'

export function useColumnResize({
  columns,
  columnsConfig,
  config,
  headerRef,
  scrollRef,
  onApply,
  onSetColumns,
  onSetResizing,
}) {
  const currentTargetColumnRef = useRef(null)
  const currentColumnsConfigRef = useRef(null)
  const initialColumnsConfigRef = useRef(null)

  useEffect(() => {
    if (columnsConfig) {
      currentColumnsConfigRef.current = columnsConfig
    }
  }, [columnsConfig])

  function handleResizeStartInternal({ event }) {
    currentTargetColumnRef.current = event.currentTarget.id
    initialColumnsConfigRef.current = columnsConfig
    currentColumnsConfigRef.current = columnsConfig

    onSetResizing(true)
  }

  function handleResizeInternal({ deltaX, event, width }) {
    const columnName = currentTargetColumnRef.current

    if (columnName) {
      const currentColumnWidth = getColumnWidth(columns, columnName)

      // if user resize last column
      if (columnName === columns[columns.length - 1].name) {
        // scroll to right side if header exceed available space
        if (
          deltaX > 0 &&
          currentColumnWidth !== width &&
          event.clientX >= headerRef.current?.getBoundingClientRect().right
        ) {
          scrollRef.current.scrollLeft += deltaX
        }
      }

      onSetColumns?.({
        ...currentColumnsConfigRef.current,
        [columnName]: {
          ...currentColumnsConfigRef.current?.[columnName],
          width: Math.round(width),
        },
      })
    }
  }

  function handleResizeEndInternal() {
    // scroll to left if header width less than available space
    if (headerRef.current?.scrollWidth < scrollRef.current?.scrollWidth) {
      scrollRef.current.scroll(
        scrollRef.current.scrollLeft -
          (scrollRef.current.scrollWidth - headerRef.current.scrollWidth),
        0
      )
    }

    onSetResizing(false)

    if (
      !isEqual(initialColumnsConfigRef.current, currentColumnsConfigRef.current)
    ) {
      onApply?.({ columns: currentColumnsConfigRef.current })
    }

    initialColumnsConfigRef.current = null
  }

  const { handleResizeStart } = useWidthResize({
    getNextWidth: ({ deltaX, startWidth }) => startWidth + deltaX,
    maxWidth: ({ currentWidth }) =>
      getColumnMaxWidth({
        columnName: currentTargetColumnRef.current,
        config,
        currentWidth,
      }),
    minWidth: ({ currentWidth }) =>
      getColumnMinWidth({
        columnName: currentTargetColumnRef.current,
        config,
        currentWidth,
      }),
    onResize: handleResizeInternal,
    onResizeEnd: handleResizeEndInternal,
    onResizeStart: handleResizeStartInternal,
  })

  function handleResize(event) {
    const columnName = event.currentTarget.id
    const currentWidth = getColumnWidth(columns, columnName)
    const { maxWidth, minWidth } = getColumnResizeLimits(config, columnName)

    currentTargetColumnRef.current = columnName

    handleResizeStart({
      event,
      width: Math.max(minWidth, Math.min(currentWidth, maxWidth)),
    })
  }

  return {
    currentColumnsConfigRef,
    currentTargetColumnRef,
    handleResize,
  }
}
